import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import {
  SentryEnvironment,
  installInnovaCoreSentryBridge,
  resolveSentryEnvironmentFromHost,
  shouldDropFrontendBreadcrumb,
} from "@/lib/sentry";
import {
  shouldEnableHostedSentrySmoke,
  shouldRunHostedSentrySmoke,
} from "@/lib/sentry-build";

export function resolveSentryEnvironment(): SentryEnvironment | null {
  const rawEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  const hostname = typeof window === "undefined" ? undefined : window.location.hostname;
  return resolveSentryEnvironmentFromHost(rawEnvironment, hostname);
}

function getSamplingByEnvironment(environment: SentryEnvironment): {
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
} {
  if (environment === "production") {
    return {
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    };
  }

  if (environment === "local") {
    return {
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
    };
  }

  return {
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.5,
    replaysOnErrorSampleRate: 1.0,
  };
}

function getTracePropagationTargets(supabaseUrl?: string): (string | RegExp)[] {
  const targets: (string | RegExp)[] = [/^\//, "localhost"];

  if (!supabaseUrl) return targets;

  const escapedUrl = supabaseUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  targets.push(new RegExp(`^${escapedUrl}`));

  return targets;
}

export function initSentry() {
  const environment = resolveSentryEnvironment();
  const release = import.meta.env.VITE_SENTRY_RELEASE?.trim() || undefined;
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim() ?? "";

  installInnovaCoreSentryBridge(environment);

  if (environment === null || environment === "local") {
    installHostedSentrySmokeTrigger(environment, release);
    return;
  }

  if (!dsn) {
    const warning = import.meta.env.DEV
      ? null
      : `VITE_SENTRY_DSN is not configured for ${environment}; hub-web Sentry is disabled.`;
    if (warning) {
      console.warn(`[Sentry] ${warning}`);
    }
    installHostedSentrySmokeTrigger(environment, release);
    return;
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const sampling = getSamplingByEnvironment(environment);

  try {
    Sentry.init({
      dsn,
      environment,
      release,
      enabled: environment !== "local",
      sendDefaultPii: false,
      beforeBreadcrumb(breadcrumb) {
        if (shouldDropFrontendBreadcrumb(breadcrumb)) {
          return null;
        }

        return breadcrumb;
      },
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.replayIntegration({
          blockAllMedia: true,
          maskAllText: true,
        }),
      ],
      tracesSampleRate: sampling.tracesSampleRate,
      replaysSessionSampleRate: sampling.replaysSessionSampleRate,
      replaysOnErrorSampleRate: sampling.replaysOnErrorSampleRate,
      tracePropagationTargets: getTracePropagationTargets(supabaseUrl),
    });
    Sentry.setTag("app_surface", "hub-web");
  } catch (error) {
    console.error("[Sentry] init failed; continuing without hosted Sentry", error);
  }
  installHostedSentrySmokeTrigger(environment, release);
}

function installHostedSentrySmokeTrigger(
  environment: SentryEnvironment | null,
  release?: string,
): void {
  const hostedSmokeEnvironment =
    environment === "development" ||
    environment === "staging" ||
    environment === "production"
      ? environment
      : null;

  if (typeof window === "undefined") {
    return;
  }

  if (
    !hostedSmokeEnvironment ||
    !shouldEnableHostedSentrySmoke(hostedSmokeEnvironment, import.meta.env.DEV)
  ) {
    delete window.__INNOVACORE_SENTRY_HOSTED_SMOKE__;
    return;
  }

  const trigger = () => {
    const error = createHostedSmokeError(hostedSmokeEnvironment, release);

    return Sentry.withScope((scope) => {
      scope.setTag("app_surface", "hub-web");
      scope.setTag("qa.sentry.kind", "hosted-build-smoke");
      scope.setContext("qa_hosted_smoke", {
        environment,
        hosted_environment: hostedSmokeEnvironment,
        release: release ?? null,
      });

      return Sentry.captureException(error);
    });
  };

  window.__INNOVACORE_SENTRY_HOSTED_SMOKE__ = trigger;

  if (shouldRunHostedSentrySmoke(window.location.search)) {
    trigger();
  }
}

function createHostedSmokeError(
  environment: SentryEnvironment,
  release?: string,
): Error {
  function triggerInnerFrame(): never {
    throw new Error(
      `innovacore.sentry.hosted-build-smoke:${environment}:${release ?? "no-release"}`,
    );
  }

  function triggerOuterFrame(): never {
    return triggerInnerFrame();
  }

  return (() => {
    try {
      triggerOuterFrame();
    } catch (error) {
      if (error instanceof Error) {
        return error;
      }

      return new Error("innovacore.sentry.hosted-build-smoke:unexpected-error");
    }

    return new Error("innovacore.sentry.hosted-build-smoke:unknown");
  })();
}
